import {
  type Options,
  parseAsArrayOf,
  parseAsBoolean,
  parseAsFloat,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryState,
} from 'nuqs'

export { useQueryState } from 'nuqs'

export function useStringArrayUrlQueryState(key: string, defaultValue: string[] = []) {
  return useQueryState(key, parseAsArrayOf(parseAsString).withDefault(defaultValue))
}

export function useStringLiteralSingleUrlQueryState<T extends readonly string[]>(
  key: string,
  validValues: T,
  defaultValue?: T[number],
) {
  return useQueryState(
    key,
    defaultValue
      ? parseAsStringLiteral<T[number]>(validValues).withDefault(defaultValue)
      : parseAsStringLiteral<T[number]>(validValues),
  )
}

export function useStringLiteralUrlQueryState(key: string, validValues: readonly string[]) {
  return useQueryState(key, parseAsArrayOf(parseAsStringLiteral(validValues)).withDefault([]))
}

export function useUniqueStringArrayUrlQueryState(key: string, defaultValue: string[] = []) {
  const [value, setValue] = useQueryState(
    key,
    parseAsArrayOf(parseAsString).withDefault(defaultValue),
  )
  const uniqueValue = Array.isArray(value) ? Array.from(new Set(value)) : value
  const setUniqueValue = (next: string[] | ((prev: string[]) => string[] | null) | null) => {
    if (typeof next === 'function') {
      void setValue((prev) => {
        const result = next(Array.isArray(prev) ? Array.from(new Set(prev)) : prev)
        return Array.isArray(result) ? Array.from(new Set(result)) : result
      })
    } else if (Array.isArray(next)) {
      void setValue(Array.from(new Set(next)))
    } else {
      void setValue(next)
    }
  }
  return [uniqueValue, setUniqueValue] as const
}

export function useUniqueStringLiteralUrlQueryState(key: string, validValues: readonly string[]) {
  const [value, setValue] = useQueryState(
    key,
    parseAsArrayOf(parseAsStringLiteral(validValues)).withDefault([]),
  )
  const uniqueValue = Array.isArray(value) ? Array.from(new Set(value)) : value
  const setUniqueValue = (next: string[] | ((prev: string[]) => string[] | null) | null) => {
    if (typeof next === 'function') {
      void setValue((prev) => {
        const result = next(Array.isArray(prev) ? Array.from(new Set(prev)) : prev)
        return Array.isArray(result) ? Array.from(new Set(result)) : result
      })
    } else if (Array.isArray(next)) {
      void setValue(Array.from(new Set(next)))
    } else {
      void setValue(next)
    }
  }
  return [uniqueValue, setUniqueValue] as const
}

export function useIntegerArrayUrlQueryState(key: string, defaultValue: number[] = []) {
  return useQueryState(key, parseAsArrayOf(parseAsInteger).withDefault(defaultValue))
}

export function useBooleanUrlQueryState(key: string, defaultValue: boolean = false) {
  return useQueryState(key, parseAsBoolean.withDefault(defaultValue))
}

export function useStringUrlQueryState(key: string, defaultValue: string = '') {
  return useQueryState(key, parseAsString.withDefault(defaultValue))
}

export function useIntegerUrlQueryState(
  key: string,
  defaultValue: number = 0,
  options: Options = {},
) {
  return useQueryState(key, parseAsInteger.withDefault(defaultValue).withOptions(options))
}

export function useFloatUrlQueryState(key: string, defaultValue: number = 0) {
  return useQueryState(key, parseAsFloat.withDefault(defaultValue))
}
